import { QueryMenu } from '@/sections/insights/queryBuilder/components/QueryBuilderSidebar/QueryMenu';
import { useUnsavedChangesPrompt } from '@/sections/insights/queryBuilder/components/QueryBuilderSidebar/useUnsavedChangesPrompt';
import { useQueryBuilder } from '@/sections/insights/queryBuilder/state/useQueryBuilder';
import { SavedQuery } from '@/sections/insights/queryBuilder/types/SavedQuery';

export interface QuerySearchResultsProps {
  /** Search results to display */
  results: SavedQuery[];
  /** Search query for empty state message */
  searchQuery: string;
}

/**
 * QuerySearchResults - Renders search results for query sidebar
 *
 * Features:
 * - Displays list of QueryMenu items
 * - Integrates with QueryBuilder context for selection
 * - Empty state with search term
 * - ARIA listbox attributes
 *
 * @example
 * ```tsx
 * <QuerySearchResults results={filteredQueries} searchQuery={searchTerm} />
 * ```
 */
export const QuerySearchResults = ({
  results,
  searchQuery,
}: QuerySearchResultsProps) => {
  const { queryState, setQuery, setMode } = useQueryBuilder();
  const { confirmIfDirty, modal } = useUnsavedChangesPrompt();

  // Empty state when no results
  if (results.length === 0) {
    return (
      <div className="text-chariot-text-secondary px-6 py-4 text-sm">
        No queries match &ldquo;{searchQuery}&rdquo;
      </div>
    );
  }

  // Handler for query selection
  const handleQueryClick = (query: SavedQuery) => {
    if (queryState?.metadata.id === query.id) return;

    confirmIfDirty(() => {
      setQuery(query);
      setMode('query');
    });
  };

  return (
    <>
      <div role="listbox" aria-label="Search results">
        {results.map(query => (
          <div
            key={query.id}
            role="option"
            tabIndex={0}
            onClick={() => handleQueryClick(query)}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleQueryClick(query);
              }
            }}
            className="hover:bg-chariot-background-secondary group flex w-full cursor-pointer items-center justify-between px-6 py-3 transition-colors"
            data-testid={`search-result-${query.id}`}
          >
            {/* Query name and description */}
            <div className="flex min-w-0 flex-1 flex-col gap-1">
              <span className="text-chariot-text-primary text-sm font-medium">
                {query.name}
              </span>
              {query.description && (
                <span className="text-chariot-text-secondary line-clamp-2 text-xs">
                  {query.description}
                </span>
              )}
            </div>

            {/* Three-dot menu - visible on hover */}
            <div className="ml-2 opacity-0 transition-opacity group-hover:opacity-100">
              <QueryMenu query={query} />
            </div>
          </div>
        ))}
      </div>
      {modal}
    </>
  );
};
